import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, LinearProgress, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Close, Download, ReceiptLong, ArrowForward, CalculateOutlined, ViewListOutlined } from '@mui/icons-material';
import { ordersAPI } from '../../Api';
import { formatOrderDate, formatUnits, saveOrdersWorkbook, summarizeExport } from './orderExport';

const { BigInt } = window;
const errorMessage = error => error?.response?.data?.data?.message || 'Не удалось выполнить запрос. Попробуйте ещё раз.';
const optionFor = order => ({
  id: order._id, orderId: order.order_id, createdAt: order.created_at,
  status: order.status, platform: order.platform || 'yandex',
});
const boundary = value => typeof value === 'string' ? { orderId: value.trim() } : { id: value?.id };
const hasValue = value => typeof value === 'string' ? Boolean(value.trim()) : Boolean(value?.id);

function BoundaryInput({ label, value, onChange, initialOptions, disabled }) {
  const [options, setOptions] = useState(initialOptions);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    if (!query.trim()) {
      setOptions(initialOptions);
      setLoading(false);
      setLookupError(false);
      return () => controller.abort();
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      setLookupError(false);
      try {
        const response = await ordersAPI.lookupExportOrders(query.trim(), { signal: controller.signal });
        if (!controller.signal.aborted) setOptions(response.data.data);
      } catch (error) {
        if (!controller.signal.aborted) setLookupError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, initialOptions]);
  return <Autocomplete
    freeSolo fullWidth disabled={disabled} value={value} options={options} loading={loading}
    filterOptions={items => items}
    getOptionLabel={option => typeof option === 'string' ? option : option.orderId || ''}
    isOptionEqualToValue={(option, selected) => option.id === selected?.id}
    onChange={(_, next) => onChange(next)}
    onInputChange={(_, input, reason) => {
      if (reason === 'input' || reason === 'clear') { setQuery(input); onChange(input); }
    }}
    renderOption={(props, option) => {
      const { key, ...rest } = props;
      return <Box component="li" {...rest} key={option.id || key} sx={{ display: 'block !important' }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{option.orderId}</Typography>
        <Typography variant="caption" color="text.secondary">{formatOrderDate(option.createdAt)} · {option.status} · {option.platform}</Typography>
      </Box>;
    }}
    renderInput={params => <TextField {...params} label={label} placeholder="Введите или вставьте ID ордера"
      helperText={lookupError ? 'Подсказки недоступны. Можно вставить полный ID.' : 'Поиск по ID среди всех обычных ордеров'}
      InputProps={{ ...params.InputProps, endAdornment: <>{loading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</> }}
    />}
  />;
}

export default function OrderExportDialog({ open, onClose, orders = [], merchants = [], platforms = [], statuses = [] }) {
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('');
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const activeRequest = useRef(null);
  const initialOptions = React.useMemo(() => orders.map(optionFor), [orders]);
  const platformOptions = React.useMemo(() => {
    const options = new Map([['yandex', { key: 'yandex', name: 'Yandex' }]]);
    for (const item of platforms) if (item.key) options.set(item.key, { key: item.key, name: item.name || item.key });
    for (const order of orders) {
      if (order.platform && !options.has(order.platform)) options.set(order.platform, { key: order.platform, name: order.platform });
    }
    return [...options.values()];
  }, [platforms, orders]);
  const statusOptions = React.useMemo(() => {
    const options = new Map(statuses.map(item => [item.value.toUpperCase(), { ...item, value: item.value.toUpperCase() }]));
    for (const order of orders) {
      const value = (order.status || 'UNKNOWN').toUpperCase();
      if (!options.has(value)) options.set(value, { value, label: value });
    }
    return [...options.values()];
  }, [statuses, orders]);
  useEffect(() => {
    if (!open) {
      activeRequest.current?.abort();
      setPhase(''); setReport(null); setError(''); setFrom(null); setTo(null); setCompleted(false);
      setPlatform(null); setSelectedMerchants([]); setSelectedStatuses([]);
    }
    return () => activeRequest.current?.abort();
  }, [open]);
  const change = (setter, value) => { setter(value); setReport(null); setCompleted(false); setError(''); };
  const close = () => { activeRequest.current?.abort(); onClose(); };
  const prepare = async () => {
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setPhase('preview'); setError(''); setReport(null); setCompleted(false);
    try {
      const response = await ordersAPI.previewExport({
        from: boundary(from), to: boundary(to),
        filters: {
          platform: platform?.key || '', merchantIds: selectedMerchants.map(item => item._id),
          statuses: selectedStatuses.map(item => item.value),
        },
      }, { signal: controller.signal });
      if (!controller.signal.aborted) setReport({
        ...response.data.data,
        filterLabels: {
          platform: platform ? `${platform.name} (${platform.key})` : 'Все платформы',
          merchants: selectedMerchants.length ? selectedMerchants.map(item => `${item.name} (${item._id})`).join(', ') : 'Все мерчанты',
          statuses: selectedStatuses.length ? selectedStatuses.map(item => `${item.label} (${item.value})`).join(', ') : 'Все статусы',
        },
      });
    } catch (err) {
      if (!controller.signal.aborted) setError(errorMessage(err));
    } finally {
      if (!controller.signal.aborted) setPhase('');
    }
  };
  const download = async () => {
    const controller = new AbortController();
    activeRequest.current = controller;
    setPhase('export'); setProgress(0); setError(''); setCompleted(false);
    try {
      const rows = [];
      const seen = new Set();
      let after = null;
      do {
        const response = await ordersAPI.exportPage({
          from: { id: report.range.from.id }, to: { id: report.range.to.id }, filters: report.range.filters, after,
        }, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const page = response.data.data;
        for (const row of page.items) {
          if (seen.has(row._id)) throw new Error('Duplicate export page');
          seen.add(row._id);
          rows.push(row);
        }
        if (page.next && (!page.items.length || JSON.stringify(page.next) === JSON.stringify(after))) throw new Error('Invalid export cursor');
        after = page.next;
        setProgress(rows.length);
      } while (after);
      const data = { rows, summary: summarizeExport(rows), range: report.range, filterLabels: report.filterLabels, generatedAt: new Date().toISOString() };
      if (controller.signal.aborted) return;
      await saveOrdersWorkbook(data, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setReport({ ...report, summary: data.summary, items: rows.slice(0, 20), generatedAt: data.generatedAt });
        setCompleted(true);
      }
    } catch (err) {
      if (!controller.signal.aborted) setError(errorMessage(err));
    } finally {
      if (!controller.signal.aborted) setPhase('');
    }
  };
  const summary = report?.summary;
  return <Dialog open={open} onClose={close} fullWidth maxWidth="lg" aria-labelledby="order-export-title"
    PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
    <DialogTitle component="div" sx={{ background: 'linear-gradient(120deg, #182844, #334879)', color: 'white', py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ bgcolor: 'rgba(255,255,255,.12)', p: 1.5, borderRadius: 2, display: { xs: 'none', sm: 'flex' } }}><ReceiptLong /></Box>
          <Box><Typography id="order-export-title" variant="h5" fontWeight={700} sx={{ color: '#fff', fontSize: { xs: 20, sm: 24 } }}>Выгрузка ордеров</Typography>
            <Typography variant="body2" sx={{ opacity: .75, mt: .5 }}>Выберите диапазон, проверьте итоги и сохраните Excel</Typography></Box>
        </Stack>
        <IconButton aria-label="Закрыть выгрузку" onClick={close} sx={{ color: 'white' }}><Close /></IconButton>
      </Stack>
    </DialogTitle>
    <DialogContent sx={{ pt: '24px !important', bgcolor: 'background.default' }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Chip size="small" label="Границы включительно" color="primary" variant="outlined" />
        <Chip size="small" label={selectedStatuses.length ? `Выбрано статусов: ${selectedStatuses.length}` : 'Все статусы'} variant="outlined" />
        <Chip size="small" label="Фильтры выгрузки задаются ниже" variant="outlined" />
      </Stack>
      <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', p: 2.5, borderRadius: 2 }}>
        <Typography variant="overline" color="text.secondary">01 / Диапазон ордеров</Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mt: .25 }}>
          <Grid item xs={12} md={5.5}><BoundaryInput label="От ордера" value={from} onChange={v => change(setFrom, v)}
            initialOptions={initialOptions} disabled={Boolean(phase)} /></Grid>
          <Grid item md={1} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', pb: 3 }}><ArrowForward color="action" /></Grid>
          <Grid item xs={12} md={5.5}><BoundaryInput label="По ордер" value={to} onChange={v => change(setTo, v)}
            initialOptions={initialOptions} disabled={Boolean(phase)} /></Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Уточнить выборку</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete options={platformOptions} value={platform} disabled={Boolean(phase)}
              getOptionLabel={item => `${item.name} (${item.key})`}
              isOptionEqualToValue={(item, selected) => item.key === selected.key}
              onChange={(_, value) => change(setPlatform, value)}
              renderInput={params => <TextField {...params} label="Платформа выгрузки" placeholder="Все платформы" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete multiple disableCloseOnSelect limitTags={2} options={merchants} value={selectedMerchants}
              disabled={Boolean(phase)} getOptionLabel={item => item.name || item._id}
              isOptionEqualToValue={(item, selected) => item._id === selected._id}
              onChange={(_, value) => change(setSelectedMerchants, value)}
              renderInput={params => <TextField {...params} label="Мерчанты выгрузки" placeholder={selectedMerchants.length ? '' : 'Все мерчанты'} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete multiple disableCloseOnSelect limitTags={2} options={statusOptions} value={selectedStatuses}
              disabled={Boolean(phase)} getOptionLabel={item => `${item.label} (${item.value})`}
              isOptionEqualToValue={(item, selected) => item.value === selected.value}
              onChange={(_, value) => change(setSelectedStatuses, value)}
              renderInput={params => <TextField {...params} label="Статусы выгрузки" placeholder={selectedStatuses.length ? '' : 'Все статусы'} />}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Можно выбрать несколько мерчантов и статусов. Пустой фильтр не ограничивает выборку.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" startIcon={<ViewListOutlined />} disabled={!orders.length || Boolean(phase)} onClick={() => {
            change(setFrom, optionFor(orders[orders.length - 1])); change(setTo, optionFor(orders[0]));
          }}>Границы текущей страницы</Button>
          <Button variant="contained" disableElevation startIcon={phase === 'preview' ? <CircularProgress size={18} color="inherit" /> : <CalculateOutlined />}
            disabled={!hasValue(from) || !hasValue(to) || Boolean(phase)} onClick={prepare}>Рассчитать диапазон</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Диапазон определяется по времени создания. Порядок ввода границ не важен. Даты отображаются по МСК.
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {phase === 'export' && <Box sx={{ my: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="body2">Подготавливаем таблицу…</Typography>
          <Typography variant="body2">{progress.toLocaleString('ru-RU')} из ~{summary.count.toLocaleString('ru-RU')} ордеров</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={Math.min(99, progress / Math.max(1, summary.count) * 100)} sx={{ borderRadius: 2, height: 6 }} />
      </Box>}
      {completed && <Alert severity="success" sx={{ mt: 2 }}>Excel сформирован: {summary.count.toLocaleString('ru-RU')} ордеров. Итоги обновлены по строкам файла.</Alert>}
      {report && <Box sx={{ mt: 3 }}>
        {summary.count === 0 && <Alert severity="info" sx={{ mb: 2 }}>В этом диапазоне нет ордеров с выбранными фильтрами.</Alert>}
        <Typography variant="overline" color="text.secondary">02 / Итоги диапазона</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {formatOrderDate(report.range.from.createdAt)} — {formatOrderDate(report.range.to.createdAt)} · МСК
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip size="small" label={report.filterLabels.platform} />
          <Chip size="small" label={report.range.filters?.merchantIds?.length ? `Мерчантов: ${report.range.filters.merchantIds.length}` : 'Все мерчанты'} />
          <Chip size="small" label={report.range.filters?.statuses?.length ? `Статусы: ${report.range.filters.statuses.join(', ')}` : 'Все статусы'} sx={{ maxWidth: '100%' }} />
        </Stack>
        <Grid container spacing={2}>
          {[
            ['Всего ордеров', summary.count, 'primary.main'],
            ['Оплачено', summary.paidCount, 'success.main'],
            ['Прочие статусы', summary.count - summary.paidCount, 'text.secondary'],
          ].map(([label, value, color]) => <Grid item xs={12} sm={4} key={label}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="h4" fontWeight={700} color={color} sx={{ mt: .5 }}>{value.toLocaleString('ru-RU')}</Typography>
            </Box>
          </Grid>)}
        </Grid>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ my: 2 }}>
          {Object.entries(summary.statuses).map(([status, count]) => <Chip key={status} size="small" variant="outlined" label={`${status}: ${count}`} />)}
        </Stack>
        {summary.currencies.map(item => <Box key={item.currency} sx={{ p: 2.5, mb: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography fontWeight={700}>{item.currency}</Typography><Chip size="small" label={`${item.count} ордеров`} />
          </Stack>
          <Grid container spacing={2}>
            {[
              ['К оплате · в выборке', item.totalUnits],
              ['Базовая сумма · в выборке', item.amountUnits],
              ['Добавочная сумма · в выборке', item.randomUnits],
              ['К оплате · оплаченные', item.paidTotalUnits],
              ['Базовая сумма · оплаченные', item.paidAmountUnits],
              ['Разница · оплаченные', String(BigInt(item.paidTotalUnits) - BigInt(item.paidAmountUnits))],
            ].map(([label, value]) => <Grid item xs={12} sm={6} md={4} key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" fontWeight={600}>{formatUnits(value)}</Typography>
            </Grid>)}
          </Grid>
          {item.invalidAmounts > 0 && <Alert severity="warning" sx={{ mt: 2 }}>Некорректные суммы у {item.invalidAmounts} ордеров: пустые значения не включены в соответствующие итоги.</Alert>}
        </Box>)}
        <Typography variant="caption" color="text.secondary">
          Базовая сумма — amount, к оплате — total_amount. Разница не учитывает комиссии и расходы.
        </Typography>
        <Divider sx={{ my: 2.5 }} />
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography fontWeight={600}>Предпросмотр таблицы</Typography>
          <Typography variant="caption" color="text.secondary">Первые {report.items.length} из {summary.count}</Typography>
        </Stack>
        <TableContainer sx={{ maxHeight: 290, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow>{['ID ордера', 'Магазин', 'Статус', 'Базовая сумма', 'К оплате', 'Валюта', 'Создан · МСК'].map(label => <TableCell key={label} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>{report.items.map(row => <TableRow key={row._id} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere', minWidth: 180 }}>{row.orderId}</TableCell>
              <TableCell>{row.merchant}</TableCell><TableCell><Chip size="small" label={row.status} color={row.status === 'PAID' ? 'success' : 'default'} variant="outlined" /></TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatUnits(row.amountUnits)}</TableCell><TableCell sx={{ whiteSpace: 'nowrap' }}>{formatUnits(row.totalUnits)}</TableCell>
              <TableCell>{row.currency}</TableCell><TableCell sx={{ whiteSpace: 'nowrap' }}>{formatOrderDate(row.createdAt)}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
      </Box>}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
      <Typography variant="caption" color="text.secondary">В Excel попадёт весь диапазон. Листы «Ордера» и «Итоги».</Typography>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={close}>{phase ? 'Отменить' : 'Закрыть'}</Button>
        <Button variant="contained" color="success" disableElevation startIcon={<Download />} sx={{ whiteSpace: 'nowrap', minWidth: 160 }} disabled={!report || Boolean(phase) || !summary?.count} onClick={download}>
          Скачать Excel
        </Button>
      </Stack>
    </DialogActions>
  </Dialog>;
}
